import { Component, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NavigationItem,
  navigationItemsInvoices,
} from '../../models/navigation-item-model';
import { LabelService } from '../../services/label.service';
import { classes, pages } from '../../shared/labels';
import { DoctorService } from '../../services/doctor.service';
import { Business, FiscalProfileOutput } from '../../models/doctor-model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ErrorPanelService } from '../../services/error-panel.service';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FISCAL_PROFILE_FIELDS } from '../../models/api/billing-data.api.model';
import { HttpErrorResponse } from '@angular/common/http';
import { StsCredentialsPanelComponent } from './sts-credentials-panel/sts-credentials-panel.component';
import { ConfirmationPanelForBusinessComponent } from './confirmation-panel-for-business/confirmation-panel-for-business.component';

@Component({
  selector: 'app-invoices-billing-data',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    StsCredentialsPanelComponent,
    ConfirmationPanelForBusinessComponent,
  ],
  templateUrl: './invoices-billing-data.component.html',
  styleUrl: './invoices-billing-data.component.scss',
})
export class InvoicesBillingDataComponent implements OnInit {
  navigationItems: NavigationItem[];
  isEditing: boolean = false;
  form: FormGroup;
  stsForm: FormGroup;
  doctorFiscalProfile!: FiscalProfileOutput;
  regimeFiscaleOptions = [
    'RF01 - Ordinario',
    'RF02 -  Contribuenti minimi (art. 1, c.96-117, L. 244/2007)',
    'RF19 - Forfettario (art.1, c. 54-89, L. 190/2014)',
  ];

  isLoading: boolean = true;
  vatNumberAlreadySet: boolean = false;
  fiscalCodeAlreadySet: boolean = false;
  showStsPassword: boolean = false;
  showStsPin: boolean = false;
  isEditingStsProfile: boolean = false;
  isSavingStsProfile: boolean = false;
  stsProfileConfigured: boolean | null = null;
  stsUsername: string | null = null;
  showConfirmationPanelForBusiness: boolean = false;
  businessDataToBeSaved: Business | null = null;

  constructor(
    public labelService: LabelService,
    private doctorService: DoctorService,
    private fb: FormBuilder,
    public errorPanelService: ErrorPanelService
  ) {
    this.navigationItems = navigationItemsInvoices.map((item) => {
      if (item.label === pages.invoices_billing_data) {
        return { ...item, class: classes.hnb };
      }
      return { ...item };
    });
    this.form = this.fb.group({
      // Generali
      first_name: [''],
      last_name: [''],
      fiscal_code: [''],
      certified_email: ['', Validators.email],
      codice_univoco: [''],
      vat_number: [
        '',
        [
          Validators.pattern(/^IT\d{11}$/),
          Validators.minLength(13),
          Validators.maxLength(13),
        ],
      ],
      regime_fiscale: [''],
      specializzazione: [''],

      // Banche
      bank_name: [''],
      iban: [''],
      bank_name_2: [''],
      iban_2: [''],

      // Indirizzi
      fiscal_address: [''],
      fiscal_street_number: [''],
      fiscal_city: [''],
      fiscal_province: ['', [Validators.pattern(/^([A-Z]{2})?$/)]],
      fiscal_zip_code: [''],
    });

    this.stsForm = this.fb.group({
      sts_username: [''],
      sts_password: [''],
      sts_pin: [''],
    });
  }

  /* ----------------- Initialization methods ----------------- */

  ngOnInit(): void {
    this.loadFiscalProfileForm();
    this.doctorService.getStsProfile().subscribe({
      next: (profile) => {
        this.stsProfileConfigured = profile.configured;
        this.stsUsername = profile.sts_username;
      },
      error: (error) => {
        console.error('Errore nel recupero della configurazione STS.', error);
        this.stsProfileConfigured = null;
        this.stsUsername = null;
      },
    });
  }

  loadFiscalProfileForm() {
    this.isLoading = true;
    this.doctorService.getFiscalProfile().subscribe({
      next: (doctorFiscalProfile: FiscalProfileOutput) => {
        this.doctorFiscalProfile = doctorFiscalProfile;
        this.form.patchValue(doctorFiscalProfile);
        switch (doctorFiscalProfile.regime_fiscale) {
          case 'RF01':
            this.form.get('regime_fiscale')?.setValue('RF01 - Ordinario');
            break;
          case 'RF02':
            this.form
              .get('regime_fiscale')
              ?.setValue(
                'RF02 -  Contribuenti minimi (art. 1, c.96-117, L. 244/2007)'
              );
            break;
          case 'RF19':
            this.form
              .get('regime_fiscale')
              ?.setValue('RF19 - Forfettario (art.1, c. 54-89, L. 190/2014)');
            break;
        }
        this.form.disable();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Errore nel caricare i dati di fatturazione.', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel caricare i dati di fatturazione.'
        );
        this.isLoading = false;
      },
    });
  }

  /* ----------------- methods related to the main form's events ----------------- */

  onCancelClick(): void {
    this.isEditing = false;
    this.loadFiscalProfileForm();
  }

  onEditClick(): void {
    this.isEditing = true;
    this.form.enable();

    // NOTE: very important. fiscal code and vat number
    // are disabled if they have already been set
    this.vatNumberAlreadySet = !!this.doctorFiscalProfile?.vat_number;
    if (this.vatNumberAlreadySet) {
      this.form.get('vat_number')?.disable();
    }
    this.fiscalCodeAlreadySet = !!this.doctorFiscalProfile?.fiscal_code;
    if (this.fiscalCodeAlreadySet) {
      this.form.get('fiscal_code')?.disable();
    }
  }

  onSaveClick(): void {
    if (this.form.valid) {
      const rawFormData = this.form.getRawValue();
      let formData = { ...this.form.value };
      if (formData.regime_fiscale && formData.regime_fiscale !== '') {
        formData.regime_fiscale = formData.regime_fiscale.split(' - ')[0]; // TODO: prettify. this is kinda ugly.
      }

      const updatedDoctorFiscalProfile: FiscalProfileOutput =
        FISCAL_PROFILE_FIELDS.reduce((obj, key) => {
          obj[key] = formData[key] || null;
          return obj;
        }, {} as FiscalProfileOutput);

      // NOTE: this endpoint updates all fields but
      // the fiscal code and vat number of the doctor
      this.doctorService
        .updateDoctorFiscalProfile(updatedDoctorFiscalProfile)
        .subscribe({
          next: () => {
            this.isEditing = false;
            this.loadFiscalProfileForm();
          },
          error: (error: HttpErrorResponse) => {
            console.error('Error:', error);
            if (error.status === 400) {
              this.errorPanelService.showErrorSnackBar(error.error.detail);
            } else {
              this.errorPanelService.showErrorSnackBar(
                "Errore nell'aggiornamento dei dati"
              );
            }
          },
        });

      // Show Confirmation Panel for Business if necessary
      const vatNow: string = rawFormData['vat_number'];
      const fiscalCodeNow: string = rawFormData['fiscal_code'];

      const gottaSetVatNumber = !this.vatNumberAlreadySet && !!vatNow;
      const gottaSetFiscalCode = !this.fiscalCodeAlreadySet && !!fiscalCodeNow;

      if (gottaSetVatNumber || gottaSetFiscalCode) {
        this.businessDataToBeSaved = {
          vat_number: vatNow,
          fiscal_code: fiscalCodeNow,
        };
        this.showConfirmationPanelForBusiness = true;
      }
    }
  }

  /* ----------------- STS form related ----------------- */

  toggleStsPasswordVisibility(): void {
    this.showStsPassword = !this.showStsPassword;
  }

  toggleStsPinVisibility(): void {
    this.showStsPin = !this.showStsPin;
  }

  onConfigureStsProfileClick(): void {
    this.isEditingStsProfile = true;
    this.stsForm.reset();
    if (this.stsProfileConfigured && this.stsUsername) {
      this.stsForm.patchValue({ sts_username: this.stsUsername });
    }
  }

  onCancelStsProfileClick(): void {
    this.isEditingStsProfile = false;
    this.stsForm.reset();
  }

  onSaveStsProfileClick(): void {
    const payload = this.stsForm.value;
    const hasAnyField =
      !!payload.sts_username || !!payload.sts_password || !!payload.sts_pin;

    if (!hasAnyField) {
      this.errorPanelService.showErrorSnackBar(
        'Inserisci almeno un campo STS da modificare'
      );
      return;
    }

    this.isSavingStsProfile = true;

    const save$ = this.stsProfileConfigured
      ? this.doctorService.updateStsProfile(payload)
      : this.doctorService.createStsProfile(payload);

    save$.subscribe({
      next: () => {
        this.isSavingStsProfile = false;
        this.isEditingStsProfile = false;
        this.stsProfileConfigured = true;
        if (payload.sts_username) {
          this.stsUsername = payload.sts_username;
        }
        this.errorPanelService.showInfoSnackBar(
          'Credenziali STS aggiornate correttamente'
        );
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error updating STS profile:', error);
        this.isSavingStsProfile = false;
        this.errorPanelService.showErrorSnackBar(
          "Errore nell'aggiornamento delle credenziali STS"
        );
      },
    });
  }
  /* ----------------- Business related -----------------  */

  onCancelBusinessChanges() {
    this.showConfirmationPanelForBusiness = false;
    this.businessDataToBeSaved = null;
  }

  onConfirmBusinessChanges() {
    this.showConfirmationPanelForBusiness = false;
    if (!this.businessDataToBeSaved) {
      return;
    }

    this.doctorService.createBusiness(this.businessDataToBeSaved).subscribe({
      next: () => {
        this.loadFiscalProfileForm();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error:', error);
        if (error.status === 400) {
          this.errorPanelService.showErrorSnackBar(error.error.detail);
        } else {
          this.errorPanelService.showErrorSnackBar(
            "Errore nell'aggiornamento dei dati"
          );
        }
      },
    });
  }
}
